import { useEffect, useRef } from 'react';
import type { SessionData } from '@/lib/api';
import { toast } from '@/store/toast';

interface Options {
  /** The shared live rows (ACTIVE_SESSION_KEY). `undefined` = not fetched yet. */
  rows: SessionData[] | undefined;
  onNavigate: (path: string) => void;
  /**
   * True while the surface is already leaving on its own (Stop / Cancel
   * in flight, or a navigation it just issued) — the drop is then
   * expected and must not trigger a second navigation.
   */
  suppressed: () => boolean;
  /** Toast shown when a pending shell vanishes without the driver cancelling it. */
  startFailedText: string;
  /** Off switch: exactly one surface owns the redirect at a time. */
  enabled?: boolean;
}

/**
 * Follows a session that ends from the charger side while the driver is
 * looking at the live view. Exactly ONE surface runs it per route:
 * HomeRoute at '/' (the home swaps to the map the moment the rows
 * empty, and the page beneath never sees the transition), ChargingPage
 * at /charging/* (where it is the only thing mounted).
 *
 *   live row (active / finishing) → gone : go to the receipt,
 *                                          /sessions/:id/complete
 *   pending row                   → gone : the start did not take
 *                                          (swept after the pending
 *                                          timeout, or refused by the
 *                                          charger). Show its detail and recovery offer.
 *
 * The two are tracked separately so a stray pending shell sitting next
 * to the live row (an abandoned owner-start on another charger) cannot
 * swallow the receipt redirect or mis-toast when it is swept later.
 * Only real transitions count: the first fetch resolving to nothing is
 * not a drop, and a fetch error keeps the last data so it cannot fake one.
 */
export function useSessionEndRedirect({ rows, onNavigate, suppressed, startFailedText, enabled = true }: Options): void {
  const lastLiveRef = useRef<string | null>(null);
  const lastPendingRef = useRef<string | null>(null);
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;

  useEffect(() => {
    if (!enabled) return;
    if (rows === undefined) return; // not fetched yet — nothing to compare
    const live = rows.find((s) => s.status === 'active' || s.phase === 'finishing');
    const pending = rows.find((s) => s.status === 'pending');

    const lastLive = lastLiveRef.current;
    const lastPending = lastPendingRef.current;
    lastLiveRef.current = live?._id ?? null;
    lastPendingRef.current = pending?._id ?? null;

    if (suppressedRef.current()) return;
    if (lastLive && !live) {
      onNavigateRef.current(`/sessions/${lastLive}/complete`);
      return;
    }
    // A pending shell that was promoted becomes the live row under the
    // same _id — that is not a failed start.
    if (lastPending && !pending && !live) {
      toast(startFailedText, { tone: 'error' });
      onNavigateRef.current(`/sessions/${lastPending}`);
    }
  }, [rows, startFailedText, enabled]);
}
